import React, { useEffect, useState, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { fetchkampeerplaatsen } from "../Reducers/actions";
import Chart from "react-apexcharts";
import { getsensorstatus } from "../../Sensors/Reducers/actions";
import { addgastengebruikmeter, finishedboeking } from "../../GastenVerbruik/Reducers/actions";
import Instellenmeter from "./Components/instellenmeter";
import InstellenmeterReserveringssysteem from "./Components/instellenmetreserveringssysteem";
import FilterBar from "./Components/Add-ons/filters";
import OfflineMetersNotification from "./Components/OfflineMetersNotification";

function FavSesnorenWidget({ kampeerplaatsen, fetchkampeerplaatsen, getsensorstatus, addgastengebruikmeter, finishedboeking, user }) {
  const [load, setLoad] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState();
  const [currentPage, setCurrentPage] = useState(0);
  const [size, setSize] = useState(12);
  const [hasMore, setHasMore] = useState(true);
  const [showModal, setShowModal] = useState(true);
  const [currentFilters, setCurrentFilters] = useState(null);
  const [sortBy, setSortBy] = useState(() => {
    // Load sorting preference from localStorage
    return localStorage.getItem('kampeerplaatsen-sort') || 'naam';
  });
  const topRef = useRef(null);
  const loadingRef = useRef(false);
  const lastScrollActivityRef = useRef(Date.now());
  const observer = useRef();

  useEffect(() => {
    // Reset and load first page when dependencies change
    setLoad(true);
    setCurrentPage(0);
    setHasMore(true);
    loadingRef.current = true;
    
    fetchkampeerplaatsen(size, search, 0, currentFilters, sortBy).then((result) => {
      setLoad(false);
      loadingRef.current = false;
      // Check if we got less than expected, meaning no more pages
      if (!result || (Array.isArray(result.results) && result.results.length < size)) {
        setHasMore(false);
      }
    });

    // Interval om elke 30 seconden opnieuw op te halen - maar niet als we recent scroll activity hebben
    const interval = setInterval(() => {
      const timeSinceLastScroll = Date.now() - lastScrollActivityRef.current;
      if (!loadingRef.current && !loadingMore && timeSinceLastScroll > 10000) {
        // Reset to first page for refresh - alleen als er geen recente scroll activiteit is (10 sec)
        setCurrentPage(0);
        setHasMore(true);
        fetchkampeerplaatsen(size, search, 0, currentFilters, sortBy);
      }
    }, 30000); // 30.000 ms = 30 seconden

    // Opruimen bij unmount
    return () => clearInterval(interval);
  }, [search, size, currentFilters, sortBy]);

  const lastKampeerplaatsRef = useCallback(node => {
    if (load || loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreKampeerplaatsen();
      }
    });
    if (node) observer.current.observe(node);
  }, [load, loadingMore, hasMore]);

  const loadMoreKampeerplaatsen = async () => {
    if (loadingMore || !hasMore || loadingRef.current) return;
    setLoadingMore(true);
    loadingRef.current = true;
    lastScrollActivityRef.current = Date.now(); // Track scroll activity
    
    const nextPage = currentPage + 1;
    try {
      const result = await fetchkampeerplaatsen(size, search, nextPage, currentFilters, sortBy);
      setCurrentPage(nextPage);
      
      // Check if we got less than expected, meaning no more pages
      if (!result || (Array.isArray(result.results) && result.results.length < size)) {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more kampeerplaatsen:', error);
    }
    setLoadingMore(false);
    loadingRef.current = false;
  };

  function graph(row) {
    // Check if there's kwh setting to calculate tegoed
    const kwh = row?.sensor?.boeking?.kwh || 0;
    const uitschakelwaarde = Number((row?.sensor?.boeking?.kwh || 0) + (row?.sensor?.boeking?.meterstanndaankomst || 0)) || 0;
    const lastvalue = row?.sensor?.lastvalue || 0;
    const hasKwhSetting = kwh > 0;
    if (hasKwhSetting) {
      // Show progress bar for credit - calculate tegoed from kwh setting
      const currentTegoed = Math.max(0, uitschakelwaarde - lastvalue);
      const maxTegoed = kwh; // Use kwh as the maximum tegoed
      const percentage = Math.min(100, Math.max(0, (currentTegoed / maxTegoed) * 100));
      const isLowCredit = percentage < 20;

      return (
        <div className="chart-container">
          <div className="credit-progress">
            <div className="credit-progress-header">
              <span className="credit-label">Tegoed</span>
              <span className={`credit-value ${isLowCredit ? "low-credit" : ""}`}>{currentTegoed.toFixed(1)} kWh</span>
            </div>
            <div className="progress-bar-container">
              <div className="progress-bar-bg">
                <div
                  className={`progress-bar-fill ${isLowCredit ? "low-credit" : ""}`}
                  style={{
                    "--target-width": `${percentage}%`,
                    animationDelay: "0.2s",
                  }}
                ></div>
              </div>
              <span className={`progress-percentage ${isLowCredit ? "low-credit" : ""}`}>{Math.round(percentage)}%</span>
            </div>
          </div>
        </div>
      );
    } else {
      // Show area chart for usage data
      const electradata = row?.sensor?.electradata || [];
      const data = [];

      if (electradata && electradata.length > 1) {
        // Bereken verbruik tussen opeenvolgende metingen
        for (let i = 1; i < electradata.length; i++) {
          const current = Number(electradata[i].value) || 0;
          const previous = Number(electradata[i - 1].value) || 0;
          const consumption = Math.abs(previous - current);
          data.push(consumption);
        }
      }

      // Als geen data, maak dummy data voor testing
      if (data.length === 0) {
        data.push(0.5, 1.2, 0.8, 1.5, 0.9, 1.1, 0.7, 1.3, 1.0, 0.6);
      }

      // Area chart configuratie
      const areaOptions = {
        chart: {
          type: "area",
          height: 80,
          sparkline: {
            enabled: true,
          },
          animations: {
            enabled: false,
          },
        },
        stroke: {
          curve: "smooth",
          width: 2,
        },
        colors: ["#848c45"],
        fill: {
          type: "gradient",
          gradient: {
            opacityFrom: 0.4,
            opacityTo: 0.1,
          },
        },
        tooltip: {
          enabled: true,
          y: {
            formatter: function (val) {
              return val.toFixed(2) + " kWh";
            },
          },
        },
      };

      const areaSeries = [
        {
          name: "Verbruik",
          data: data.slice(-20), // Laatste 20 punten
        },
      ];

      return (
        <div className="chart-container">
          <Chart options={areaOptions} series={areaSeries} type="area" height={80} width="100%" />
        </div>
      );
    }
  }

  function getCreditBulletColor(sensor) {
    const credit = getCredit(sensor);

    if (credit <= 0) return "red";
    if (credit < 2) return "orange";
    if (credit >= 2) return "green";
    return "hidden";
  }

  function getCredit(sensor) {
    // Als er een boeking is, bereken tegoed op basis van beginstand + gekochte kWh - huidige meterstand
    if (sensor?.boeking) {
      const beginstand = Number(sensor.boeking.meterstanndaankomst) || 0;
      const gekochteKwh = Number(sensor.boeking.kwh) || 0;
      const huidigeMeterstand = Number(sensor.lastvalue) || 0;
      
      return Number((beginstand + gekochteKwh - huidigeMeterstand).toFixed(2));
    }
    
    // Oude berekening voor sensors zonder boeking
    return sensor?.kwh > 0 ? Number((sensor.uitschakelwaarde - sensor.lastvalue).toFixed(2)) : 0;
  }

  const handleSubmit = (vals, _, props) => {
    setShowModal({ text: "We zijn de meter aan het instellen" });

    addgastengebruikmeter(vals).then((data) => {
      if (data.type === "ADD_GASTEN_VERBRUIK") {
        setCurrentPage(0);
        setHasMore(true);
        fetchkampeerplaatsen(size, search, 0, currentFilters, sortBy).then(() => {
          setShowModal(false);
          props.reset(); // Reset het formulier
        });
      } else {
        alert("Er is een fout opgetreden bij het instellen van de meter");
      }
    });
  };

  const boekingafronden = (id, vals) => {
    setShowModal({ text: "We zijn de boeking aan het afronden" });
    finishedboeking(id, vals).then((data) => {
      if (data.type === "ADD_GASTEN_VERBRUIK") {
        setCurrentPage(0);
        setHasMore(true);
        fetchkampeerplaatsen(size, search, 0, currentFilters, sortBy).then((data) => {
          setShowModal(false);
        });
      } else {
        alert("Er is een fout opgetreden bij het afronden van de boeking");
      }
    });
  };

  const reloadplaatsen = () => {
    setCurrentPage(0);
    setHasMore(true);
    fetchkampeerplaatsen(size, search, 0, currentFilters, sortBy).then(() => {
      setLoad(false);
    });
  };
  const handleSearchChange = (e) => {
    if (e.target.value.length > 1 || e.target.value.length === 0) {
      setLoad(true);
      setCurrentPage(0);
      setHasMore(true);
      setSearch(e.target.value);
    } else {
      setSearch(e.target.value);
    }
  };


  function getVerbruik(sensor) {
    const laatsteWaarde = sensor?.lastvalue ? Number(sensor.lastvalue) : null;
    const beginstand = sensor?.boeking?.meterstanndaankomst ? Number(sensor.boeking.meterstanndaankomst) : null;

    if (laatsteWaarde !== null && beginstand !== null) {
      return (laatsteWaarde - beginstand).toFixed(2);
    }

    return null; // Geen verbruik bekend
  }
  const handleFilterChange = React.useCallback(
    (filters) => {
      // Vergelijk filters om onnodige calls te voorkomen
      if (JSON.stringify(filters) !== JSON.stringify(currentFilters)) {
        loadingRef.current = true;
        setLoad(true);
        setCurrentPage(0);
        setHasMore(true);
        setCurrentFilters(filters);
      }
    },
    [size, search, fetchkampeerplaatsen, currentFilters]
  );

  const handleSortChange = React.useCallback(
    (newSortBy) => {
      // Save sort preference to localStorage
      localStorage.setItem('kampeerplaatsen-sort', newSortBy);
      
      // Update state and trigger reload
      setLoad(true);
      setCurrentPage(0);
      setHasMore(true);
      setSortBy(newSortBy);
    },
    []
  );
  return (
    <div className="row gx-0" ref={topRef}>
      <OfflineMetersNotification />
      <div className="col-12 p-3">
        <FilterBar onFilterChange={handleFilterChange} onSearchChange={handleSearchChange} onSortChange={handleSortChange} search={search} sortBy={sortBy} />
      </div>
      {load ? (
        <div className="col-lg-size">
          <div className="alert alert-info">Laden...</div>
        </div>
      ) : (
        <>
          {kampeerplaatsen.length > 0 &&
            kampeerplaatsen.map((sensor, key) => (
              <div 
                key={key} 
                className="col-lg-4 col-md-6 col-sm-12 d-flex p-3"
                ref={key === kampeerplaatsen.length - 1 ? lastKampeerplaatsRef : null}
              >
                <div className={`campsite-card ${!sensor?.boeking?.gastinfo ? "unbooked" : ""} ${!sensor.active ? "disabled" : ""}`}>
              {/* Header with location number */}
              <div className="campsite-header">
                <div className="location-number">{sensor.naam}</div>
                <div className="power-status">
                  {sensor?.sensor?.electra?.status === "processing" ? (
                    <div className="status-processing">
                      <div className="spinner-sm"></div>
                      <span>Verwerken...</span>
                    </div>
                  ) : (
                    <div className={`power-indicator ${sensor?.sensor?.electra?.ingeschakeld ? "on" : "off"}`}>
                      <div className="power-icon"></div>
                      <span>{sensor?.sensor?.electra?.ingeschakeld ? "Ingeschakeld" : "Uitgeschakeld"}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Family name if booked */}
              
              {sensor?.sensor?.boeking?.gastinfo && (
                <div className="family-name">{sensor?.sensor?.boeking?.gastinfo?.achternaam ? `${sensor?.sensor?.boeking?.gastinfo?.tussennaam || ""} ${sensor?.sensor?.boeking?.gastinfo?.achternaam}`.trim() : sensor?.sensor?.boeking?.gastinfo?.bootnaam || "Bootnaam onbekend"}</div>
              )}

              {/* Sparkline chart */}
              {graph(sensor)}

              {/* Credit and usage info */}
              <div className="usage-info">
                <div className="usage-row">
                  <span className="usage-label">Tegoed:</span>
                  <div className="credit-info">
                    <span className={`credit-bullet ${getCreditBulletColor(sensor.sensor)}`}></span>
                    <span className="credit-amount">{sensor?.sensor?.boeking ? `${getCredit(sensor.sensor)} kWh` : "Geen tegoed"}</span>
                  </div>
                </div>

                <div className="usage-row">
                  <span className="usage-label">{sensor?.sensor?.boeking ? "Verbruik:" : "Meterstand:"}</span>
                  <span className="usage-value">
                    {sensor?.sensor?.boeking 
                      ? `${getVerbruik(sensor.sensor) || "0.00"} kWh`
                      : `${sensor?.sensor?.lastvalue || "0.00"} kWh`
                    }
                  </span>
                </div>
                {sensor?.sensor?.electra?.ampere && (
                  <div className="usage-row">
                    <span className="usage-label">Amperage:</span>
                    <span className="amperage-value">{sensor?.sensor?.electra?.ampere} A</span>
                  </div>
                )}
              </div>

              {/* Power status */}
              

              {/* Payment status indicator */}
              {sensor?.sensor?.boeking && !sensor?.sensor?.boeking?.betaald && (
                <div className="payment-warning">
                  <i className="fas fa-exclamation-triangle"></i>
                  Niet betaald
                </div>
              )}

              {/* Settings button */}
              <div className="card-footer-new">
                <div className="card-footer">
                  {user?.bedrijf?.reserveringssysteem ? (
                    <InstellenmeterReserveringssysteem boekingafronden={boekingafronden} showModal={showModal} sensor={sensor} onSubmit={handleSubmit} reloadplaatsen={reloadplaatsen} plaatsen={kampeerplaatsen} />
                  ) : (
                    <Instellenmeter boekingafronden={boekingafronden} showModal={showModal} sensor={sensor} onSubmit={handleSubmit} reloadplaatsen={reloadplaatsen} plaatsen={kampeerplaatsen} />
                  )}
                </div>
              </div>
            </div>
          </div>
            ))}
          
          {/* Loading indicator for infinite scroll */}
          {loadingMore && (
            <div className="col-lg-12">
              <div className="alert alert-info text-center">
                <i className="fas fa-spinner fa-spin"></i> Meer kampeerplaatsen laden...
              </div>
            </div>
          )}
          
          {/* No more results indicator */}
          {!hasMore && kampeerplaatsen.length > 0 && (
            <div className="col-lg-12">
              <div className="alert alert-secondary text-center">
                <i className="fas fa-info-circle"></i> Alle kampeerplaatsen zijn geladen
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
function mapStateToProps(state) {
  return { user: state.auth.user, kampeerplaatsen: state.standplaatsen.kampeerplaatsen };
}
export default connect(mapStateToProps, { fetchkampeerplaatsen, getsensorstatus, addgastengebruikmeter, finishedboeking })(FavSesnorenWidget);
